import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dropdown, Input } from 'semantic-ui-react'
import { TemplateService } from 'utility'
import {
    ensureFormulaSuffix,
    formatFormulaSaveError,
    readFormulaCheck,
} from 'utility/formula'

const Panel = styled.div`
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #dbe3f0;
    border-left: 3px solid #2841a1;
    border-radius: 12px;
    background: #f8fafc;
`

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
`

const Title = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: #1f2937;
`

const FxMark = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 7px;
    border-radius: 999px;
    background: #2841a1;
    color: #fff;
    font-size: 12px;
    letter-spacing: 0.02em;
`

const Tabs = styled.div`
    display: inline-flex;
    padding: 3px;
    border-radius: 10px;
    background: #e8eef8;
`

const Tab = styled.button`
    border: 0;
    background: ${props => (props.$active ? '#fff' : 'transparent')};
    color: ${props => (props.$active ? '#2841a1' : '#4b5563')};
    font-weight: 650;
    font-size: 13px;
    padding: 7px 12px;
    border-radius: 8px;
    cursor: pointer;
    box-shadow: ${props => (props.$active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none')};
`

const Label = styled.label`
    display: block;
    margin: 12px 0 6px;
    font-size: 13px;
    font-weight: 650;
    color: #374151;
`

const Area = styled.textarea`
    width: 100%;
    min-height: 84px;
    resize: vertical;
    border-radius: 10px;
    border: 1px solid ${props => (props.$invalid ? '#f0b4b4' : '#d5deea')};
    padding: 10px 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    background: #fff;
    color: #111827;
`

const Help = styled.p`
    margin: 6px 0 0;
    color: #6b7280;
    font-size: 12px;
`

const CellList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
`

const CellRow = styled.div`
    display: grid;
    grid-template-columns: 72px 1fr;
    gap: 10px;
    align-items: center;
`

const CellCode = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #d5deea;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-weight: 700;
    color: #2841a1;
`

const MetaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 4px;
`

const MetaField = styled.div`
    min-width: 120px;
`

const PreviewGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 8px;
`

const Status = styled.div`
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${props => (props.$ok ? '#ecfdf3' : '#fef2f2')};
    border: 1px solid ${props => (props.$ok ? '#b7ebc9' : '#f5c2c2')};
    color: ${props => (props.$ok ? '#166534' : '#991b1b')};
`

const PreviewValue = styled.div`
    margin-top: 4px;
    font-size: 1.25rem;
    font-weight: 750;
`

const Stored = styled.div`
    margin-top: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    word-break: break-word;
`

const Checking = styled.div`
    margin-top: 8px;
    color: #6b7280;
    font-size: 12px;
`

const isUnmappedCellError = item => {
    const code = String(item?.Code || '').toUpperCase()
    if (code === 'CELL_NOT_MAPPED') return true
    return /^pick a question\b/i.test(String(item?.Message || ''))
}

const clampDecimals = value => {
    const number = Number(value)
    if (!Number.isFinite(number)) return 0
    return Math.max(0, Math.min(6, Math.trunc(number)))
}

const emptyDraft = questionId => ({
    questionId,
    source: 'excel',
    excelText: '',
    codesText: '',
    cellMap: {},
    decimals: 1,
    suffix: '',
    expr: '',
    valid: false,
    checking: false,
    error: '',
})

export const FormulaEditor = ({
    templateId,
    questionId,
    questionLabel,
    initialFormula,
    catalog,
    onChange,
}) => {
    const initial = initialFormula || {}
    const initialSource = initial.Source === 'codes' ? 'codes' : 'excel'
    const [source, setSource] = useState(initialSource)
    const [excelText, setExcelText] = useState(
        initialSource === 'excel' ? initial.ExcelText || '' : '',
    )
    const [codesText, setCodesText] = useState(
        initialSource === 'codes' ? initial.ExcelText || '' : '',
    )
    const [cellMap, setCellMap] = useState(initial.CellMap || {})
    const [decimals, setDecimals] = useState(
        initial.Decimals == null ? 1 : clampDecimals(initial.Decimals),
    )
    const [suffix, setSuffix] = useState(initial.Suffix || '')
    const [samples, setSamples] = useState({})
    const [checking, setChecking] = useState(false)
    const [check, setCheck] = useState(null)
    const [requestError, setRequestError] = useState('')
    const [formulaFocused, setFormulaFocused] = useState(false)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const requestSerial = useRef(0)

    const text = source === 'excel' ? excelText : codesText
    const options = catalog?.options || []
    const catalogRef = useRef(catalog)
    catalogRef.current = catalog

    const publish = next => {
        if (onChangeRef.current) onChangeRef.current(next)
    }

    useEffect(() => {
        const trimmed = String(text || '').trim()
        if (!trimmed) {
            setChecking(false)
            setCheck(null)
            setRequestError('')
            publish({
                ...emptyDraft(questionId),
                source,
                excelText,
                codesText,
                cellMap,
                decimals,
                suffix: String(suffix || '').trim().slice(0, 20),
                error: 'Enter a formula.',
            })
            return undefined
        }
        if (!templateId || !questionId) {
            publish({
                ...emptyDraft(questionId),
                source,
                excelText,
                codesText,
                cellMap,
                decimals,
                suffix: String(suffix || '').trim().slice(0, 20),
                error: 'Save the template once so this question can be checked.',
            })
            return undefined
        }

        const handle = setTimeout(async () => {
            const serial = requestSerial.current + 1
            requestSerial.current = serial
            setChecking(true)
            setRequestError('')
            const sampleValues = {}
            Object.keys(samples).forEach(key => {
                const value = samples[key]
                if (value === '' || value == null) return
                const numeric = Number(value)
                if (Number.isFinite(numeric)) sampleValues[key] = numeric
            })
            const payload = {
                TemplateId: templateId,
                QuestionId: questionId,
                Mode: source,
                Text: text,
                Decimals: decimals,
                Questions: catalogRef.current?.questions || [],
            }
            if (source === 'excel') payload.CellMap = cellMap || {}
            if (Object.keys(sampleValues).length) payload.SampleValues = sampleValues
            try {
                const raw = await TemplateService.checkFormula(payload)
                if (serial !== requestSerial.current) return
                const parsed = readFormulaCheck(raw)
                setCheck(parsed)
                const mappingErrors = parsed.errors.filter(isUnmappedCellError)
                const otherErrors = parsed.errors.filter(item => !isUnmappedCellError(item))
                const hasQuestionOptions = (catalogRef.current?.options || []).length > 0
                const otherMessage = otherErrors.map(item => item.Message).filter(Boolean).join(' ')
                const unmappedMessage = mappingErrors.map(item => item.Message).filter(Boolean).join(' ')
                const canSkipMapping = !hasQuestionOptions && mappingErrors.length > 0 && !otherMessage
                publish({
                    questionId,
                    source,
                    excelText,
                    codesText,
                    cellMap,
                    decimals,
                    suffix: String(suffix || '').trim().slice(0, 20),
                    expr: parsed.expr,
                    valid: (parsed.valid && !!parsed.expr) || canSkipMapping,
                    checking: false,
                    error: otherMessage,
                    unmappedMessage: hasQuestionOptions ? unmappedMessage : '',
                    hasQuestionOptions,
                })
            } catch (error) {
                if (serial !== requestSerial.current) return
                const message =
                    formatFormulaSaveError(error) ||
                    'The formula could not be checked. Please try again.'
                setCheck(null)
                setRequestError(message)
                publish({
                    questionId,
                    source,
                    excelText,
                    codesText,
                    cellMap,
                    decimals,
                    suffix: String(suffix || '').trim().slice(0, 20),
                    expr: '',
                    valid: false,
                    checking: false,
                    error: message,
                })
            } finally {
                if (serial === requestSerial.current) setChecking(false)
            }
        }, 450)

        publish({
            questionId,
            source,
            excelText,
            codesText,
            cellMap,
            decimals,
            suffix: String(suffix || '').trim().slice(0, 20),
            expr: '',
            valid: false,
            checking: true,
            error: '',
        })

        return () => clearTimeout(handle)
    }, [
        templateId,
        questionId,
        source,
        excelText,
        codesText,
        cellMap,
        decimals,
        suffix,
        samples,
        catalog,
    ])

    const blockingErrors = (check?.errors || []).filter(item => !isUnmappedCellError(item))
    const showInvalid = !!requestError || blockingErrors.length > 0
    const showFormulaFeedback = !formulaFocused
    const cells = check?.cells || []
    const inputs = check?.inputs || []
    const previewText = useMemo(() => {
        const display = check?.preview?.Display ?? check?.preview?.display
        const value = check?.preview?.Value ?? check?.preview?.value
        const raw =
            display != null && String(display).trim() !== ''
                ? String(display)
                : value
        if (raw == null || raw === '') return ''
        return ensureFormulaSuffix(String(raw), suffix)
    }, [check, suffix])

    return (
        <Panel>
            <TitleRow>
                <Title>
                    <FxMark>fx</FxMark>
                    Formula{questionLabel ? `: ${questionLabel}` : ''}
                </Title>
                <Tabs>
                    <Tab
                        type="button"
                        $active={source === 'excel'}
                        onClick={() => setSource('excel')}
                    >
                        Paste from Excel
                    </Tab>
                    <Tab
                        type="button"
                        $active={source === 'codes'}
                        onClick={() => setSource('codes')}
                    >
                        Type with question numbers
                    </Tab>
                </Tabs>
            </TitleRow>

            <Label>{source === 'excel' ? 'Excel formula' : 'Formula'}</Label>
            <Area
                $invalid={showFormulaFeedback && showInvalid}
                value={text}
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                data-gramm="false"
                data-enable-grammarly="false"
                placeholder={
                    source === 'excel'
                        ? '=ROUND((B2-C2)/C2*100,1)'
                        : '=ROUND(([1]-[2])/[2]*100,1)'
                }
                onFocus={() => setFormulaFocused(true)}
                onBlur={() => setFormulaFocused(false)}
                onChange={event => {
                    const next = event.target.value
                    if (source === 'excel') setExcelText(next)
                    else setCodesText(next)
                }}
            />
            <Help>
                {source === 'excel'
                    ? 'Paste a formula, then choose which Number, Text, or Formula question each cell is.'
                    : 'Use the question number in brackets, for example [1] and [2].'}
            </Help>

            {source === 'excel' && cells.length > 0 && (
                <>
                    <Label>Which question is each cell?</Label>
                    <CellList>
                        {cells.map(cell => (
                            <CellRow key={cell.Cell}>
                                <CellCode>{cell.Cell}</CellCode>
                                <Dropdown
                                    selection
                                    search
                                    fluid
                                    placeholder="Pick a question"
                                    options={options}
                                    value={cellMap[cell.Cell] || cell.QuestionId || ''}
                                    onChange={(event, data) => {
                                        setCellMap(prev => ({
                                            ...prev,
                                            [cell.Cell]: data.value || '',
                                        }))
                                    }}
                                />
                            </CellRow>
                        ))}
                    </CellList>
                    {!options.length && (
                        <Help>Add a Number or Text question before this formula can be calculated.</Help>
                    )}
                </>
            )}

            <MetaRow>
                <MetaField>
                    <Label>Decimals</Label>
                    <Dropdown
                        selection
                        compact
                        options={[0, 1, 2, 3, 4, 5, 6].map(value => ({
                            key: value,
                            value,
                            text: String(value),
                        }))}
                        value={decimals}
                        onChange={(event, data) => setDecimals(clampDecimals(data.value))}
                    />
                </MetaField>
                <MetaField>
                    <Label>Unit (optional)</Label>
                    <Input
                        value={suffix}
                        maxLength={20}
                        placeholder="%"
                        onChange={(event, data) => setSuffix(String(data.value || '').slice(0, 20))}
                    />
                </MetaField>
            </MetaRow>

            {inputs.length > 0 && (
                <>
                    <Label>Preview with</Label>
                    <PreviewGrid>
                        {inputs.map(input => (
                            <MetaField key={input.QuestionId}>
                                <Help style={{ marginBottom: 4 }}>
                                    {input.Number != null ? `[${input.Number}] ` : ''}
                                    {input.Label || 'Value'}
                                </Help>
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    step="any"
                                    value={samples[input.QuestionId] ?? ''}
                                    placeholder="0"
                                    onChange={(event, data) => {
                                        setSamples(prev => ({
                                            ...prev,
                                            [input.QuestionId]: data.value,
                                        }))
                                    }}
                                />
                            </MetaField>
                        ))}
                    </PreviewGrid>
                </>
            )}

            {showFormulaFeedback && checking && <Checking>Checking formula…</Checking>}

            {showFormulaFeedback && requestError && <Status>{requestError}</Status>}

            {showFormulaFeedback && !requestError && check && check.valid && (
                <Status $ok>
                    Valid
                    {check.displayText ? <Stored>{check.displayText}</Stored> : null}
                    {previewText ? <PreviewValue>Preview: {previewText}</PreviewValue> : null}
                </Status>
            )}

            {showFormulaFeedback && !requestError && blockingErrors.length > 0 && (
                <Status>
                    {blockingErrors.map(item => item.Message).join(' ')}
                </Status>
            )}
        </Panel>
    )
}
